import { LitElement, html, css, nothing, type CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

/**
 * The "Visning" menu in the standalone preview's head row: one button, and everything about HOW the
 * page is drawn behind it (`Previews huvud samlas`, projekt-i-kalkylatorn 18).
 *
 * <p><b>Why the controls are slotted rather than owned.</b> They are the base preview's own buttons
 * (`_renderViewControls`), bound to its state; owning a second copy here would be two sets of
 * view controls to keep in step. So this element owns the opening, the closing and the panel, and
 * nothing about what is in it.</p>
 *
 * <p>The panel's content is rendered only while the menu is OPEN — an author who has not asked for
 * the menu can neither see nor tab into it.</p>
 */
@customElement('ixit-preview-view-menu')
export class IxitPreviewViewMenu extends LitElement {
    static styles: CSSResultGroup = css`
        :host {
            position: relative;
            display: inline-flex;
        }
        /* A glyph, not a uui-icon: this page loads no Umbraco icon registry, so a named icon renders
           as nothing at all — and the head row says everything else in glyphs (✎, ▣, ▾) already. */
        .view-menu-glyph {
            margin-right: 6px;
        }
        .view-menu-panel {
            position: absolute;
            top: 100%;
            right: 0;
            z-index: 4;
            display: flex;
            flex-direction: column;
            gap: 8px;
            min-width: 260px;
            padding: 10px;
            border: 1px solid var(--uui-color-border);
            border-radius: var(--uui-border-radius, 4px);
            background: var(--uui-color-surface);
            color: var(--uui-color-text);
        }
    `;

    /** What the button says beside its icon. The word, not the icon, is what carries the meaning. */
    @property({ type: String }) label = 'Visning';

    @state() private _open = false;

    /** Whether the menu stands open — read by the page's tests and by whoever wants to close it. */
    get open(): boolean {
        return this._open;
    }

    /** A click anywhere else closes the menu. On the document, because the click the author makes
     *  to leave is by definition not on this element. */
    private readonly _onDocumentClick = (event: Event) => {
        if (!this._open) return;
        if (event.composedPath().includes(this)) return;
        this._open = false;
    };

    /** Escape closes it, and the button keeps the focus — the author is still where they were. */
    private readonly _onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !this._open) return;
        this._open = false;
        this.renderRoot.querySelector<HTMLElement>('.view-menu-toggle')?.focus();
    };

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('click', this._onDocumentClick);
        this.addEventListener('keydown', this._onKeyDown);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('click', this._onDocumentClick);
        this.removeEventListener('keydown', this._onKeyDown);
    }

    render() {
        return html`
            <uui-button
                class="view-menu-toggle"
                look="secondary"
                compact
                label="Visning — läge, layout och tema"
                title="Visning — läge, layout och tema"
                aria-haspopup="menu"
                aria-expanded=${this._open ? 'true' : 'false'}
                @click=${() => { this._open = !this._open; }}
            >
                <span class="view-menu-glyph" aria-hidden="true">☰</span>
                ${this.label}
            </uui-button>
            ${this._open ? html`
                <div class="view-menu-panel" role="menu"><slot></slot></div>
            ` : nothing}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ixit-preview-view-menu': IxitPreviewViewMenu;
    }
}
